import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { Header } from '@components/Header';
import { PageErrorState } from '@components/core-ui/PageErrorState';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useQuery } from '@tanstack/react-query';
import { OrganizationTeamWithMembersResponseDto } from 'app/API';
import { adminWorkspacesKeys } from 'app/queries/adminWorkspaces/adminWorkspacesKeys';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router';
import { createFetchAdminWorkspaceQueryFn } from './api/fetchAdminWorkspace';
import { createFetchAdminWorkspaceTeamsQueryFn } from './api/fetchAdminWorkspaceTeams';
import { TeamCards } from './components/TeamCards';
import { WorkspaceDetailsForm } from './form';

import { Wrapper } from './styles/Wrapper';

const EMPTY_TEAMS: OrganizationTeamWithMembersResponseDto[] = [];

export const AdminWorkspaceDetailsPage = () => {
  const { call } = useAPI();
  const { id: workspaceId = '' } = useParams<{ id: string }>();
  const [teamsDisclosureOpen, setTeamsDisclosureOpen] = useState<boolean>(true);

  const workspaceQuery = useQuery({
    queryKey: adminWorkspacesKeys.detail(workspaceId),
    queryFn: createFetchAdminWorkspaceQueryFn(call),
    enabled: workspaceId !== '',
  });
  const workspace = workspaceQuery.data?.result;

  const teamsQuery = useQuery({
    queryKey: adminWorkspacesKeys.teamsByWorkspace(workspaceId),
    queryFn: createFetchAdminWorkspaceTeamsQueryFn(call),
    enabled: workspace !== undefined,
  });
  const teams = teamsQuery.data?.result ?? EMPTY_TEAMS;

  const teamsTitle = `Teams${teams.length > 0 ? ` (${teams.length})` : ''}`;

  return (
    <>
      <Helmet>
        <title>{workspace?.name || 'Workspace'} - AutoScript Admin</title>
      </Helmet>
      <Header title={workspace?.name} backRoute={routes.adminOrganizationDetails.make(workspace?.organizationId)} />

      {workspaceQuery.isLoading ? <AbsoluteSpinner overlay={true} /> : null}

      {workspaceQuery.isError ? (
        <PageErrorState
          message="Couldn't load workspace"
          actionLabel="Retry"
          actionDisabled={workspaceQuery.isFetching}
          onAction={() => void workspaceQuery.refetch()}
        />
      ) : null}

      {workspace ? (
        <Wrapper>
          {teamsQuery.isLoading ? <AbsoluteSpinner overlay={true} /> : null}
          <WorkspaceDetailsForm name={workspace.name ?? ''} slug={workspace.slug ?? ''} />

          <div>
            <DisclosureSection
              title={teamsTitle}
              open={teamsDisclosureOpen}
              setOpenState={setTeamsDisclosureOpen}
              loading={false}
            >
              {teamsQuery.isError ? (
                <PageErrorState
                  message="Couldn't load teams"
                  actionLabel="Retry"
                  actionDisabled={teamsQuery.isFetching}
                  onAction={() => void teamsQuery.refetch()}
                />
              ) : (
                <TeamCards teams={teams} />
              )}
            </DisclosureSection>
          </div>
        </Wrapper>
      ) : null}
    </>
  );
};
